import React, { useState } from 'react'
import { TabPanel } from 'primereact/tabview'
import { ProgressBar } from 'primereact/progressbar'
import { useIntl } from 'react-intl'
import { DataTable, Modal, ResponsiveSidebar, ToolPanel, TabView } from 'Components/readonly'
import { PageConstraint, PageHeader, Section, Spacer, FlexRowCenter } from 'Components/layout'
import { Button, SearchInput, SelectButton, Dropdown } from 'Components/input'
import useGetData from 'Hooks/useGetData'
import useModalController from "../../../Hooks/useModalController";
import SdsRegisterDialog from "Components/sdsRegisterDialog";
import { Column } from "../../../Components/readonly";
import PdfRevision from "../components/pdfRevision";
import styled from 'styled-components'
import { globalSearch } from 'Utils/search'
import axios from 'axios'

const Status = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;

  &.status-discarded {
    color: #CC3E3E;
    .circle {
      background-color: #CC3E3E;
    }
  }

  &.status-unknown {
    .circle {
      background-color: #FFF;
    }
  }

  &.status-exact {
    color: #3BB377;
    .circle {
      background-color: #3BB377;
    }
  }

  &.status-candidate {
    color: #DCC924;
    .circle {
      background-color: #DCC924;
    }
  }

  && {
    .circle {
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }
  }
`

const ProgressBarsWrapper = styled.div`
  .p-progressbar {
    .p-progressbar-value {
      background: #3bb377
    }
  }
`

const TableControls = styled(FlexRowCenter)`
  @media screen and (max-width: 640px) {
    .search-container {
      width: 100%;
    }

    ${SelectButton}.p-selectbutton {
      padding-top: 10px;
      display: flex;
      flex: 1 0 auto;
    }
  }

  @media screen and (max-width: 992px) {
    flex-wrap: wrap;
  }
`

export const SdsRegistryPage = () => {
  const intl = useIntl()
  const [pageKey, setPageKey] = useState(0);
  const [isLoadingStats, stats] = useGetData('/rapi/admin/sds-registry/stats', [], [pageKey]);
  const [isLoading, sdsRegistries] = useGetData('/rapi/admin/sds-registry', [], [pageKey]);
  const [isLoadingCrawls, crawls] = useGetData('/rapi/admin/sds-registry/crawls', [], [pageKey]);
  const [sdsRegistryDialog] = useModalController({ title: intl.formatMessage({ id: 'chemicalsds.registry.dialog.header' }) })
  const [product, setProduct] = useState(null);
  const [inputFilter, setInputFilter] = useState('');
  const [orgInputFilter, setOrgInputFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgStatusFilter, setOrgStatusFilter] = useState('all');
  const [organisationId, setOrganisationId] = useState(null); // int
  const [organisations, setOrganisations] = useState([]); // For dropdown
  const isOrgSelected = !!organisationId;
  const [sdsId] = useState(0);

  const [isLoadingOrgChemicals, orgChemicals, setOrgChemicals] = useGetData(
    organisationId
      ? `/rapi/admin/sds-registry/organisation/${organisationId}`
      : null,
    [],
    [organisationId]
  );

  const [isLoadingOrgStats, orgStats, setOrgStats] = useGetData(
    organisationId
      ? `/rapi/admin/sds-registry/organisation/${organisationId}/stats`
      : null,
    [],
    [organisationId]
  );

  const statusFilterOptions = [
    { label: intl.formatMessage({ id: 'chemicalsds.registry.filter.all' }), value: 'all' },
    { label: intl.formatMessage({ id: 'chemicalsds.registry.filter.identical' }), value: 'exact' },
    { label: intl.formatMessage({ id: 'chemicalsds.registry.filter.newer' }), value: 'candidate' },
    { label: intl.formatMessage({ id: 'chemicalsds.registry.filter.other' }), value: 'other' }
  ]

  React.useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const response = await axios.get('/rapi/admin/sds-registry/organisations');
        setOrganisations(response.data);
      } catch (error) {
        console.error('Failed to load organisations:', error);
      }
    };

    fetchOrganisations();
  }, []); // empty array - only run once

  const handleInputFilter = (event) => {
    setInputFilter(event.target.value)
  }

  const handleOrgInputFilter = (event) => {
    setOrgInputFilter(event.target.value)
  }

  const updateStatusFilter = (e) => {
    if (e.value) {
      setStatusFilter(e.value)
    }
  }

  const updateOrgStatusFilter = (e) => {
    if (e.value) {
      setOrgStatusFilter(e.value)
    }
  }

  const filteredSdsRegistries = React.useMemo(() => {
    const searchResults = globalSearch([...sdsRegistries], inputFilter, [
      'chemical_id',
      'chemical_name',
      'supplier',
      'crawl_date'
    ]);

    const filtered = searchResults.filter(item => {
      if (!statusFilter || statusFilter === 'all') return true;
      return item.status_flag === statusFilter;
    });

    return filtered;

  }, [inputFilter, statusFilter, sdsRegistries])

  const filteredOrgChemicals = React.useMemo(() => {
    const searchResults = globalSearch([...orgChemicals], orgInputFilter, [
      'chemical_id',
      'chemical_name',
      'supplier',
      'crawl_date'
    ]);

    const filtered = searchResults.filter(item => {
      if (!orgStatusFilter || orgStatusFilter === 'all') return true;
      return item.status_flag === orgStatusFilter;
    });

    return filtered;

  }, [orgInputFilter, orgStatusFilter, orgChemicals])

  const filteredCrawls = React.useMemo(() => {
    const searchResults = globalSearch([...crawls], inputFilter, [
      'chemical_id',
      'chemical_name',
      'chemical_supplier',
      'date'
    ]);

    return searchResults;

  }, [inputFilter, crawls])

  /**
   * Reloads the current view by performing the following actions:
   * - Closes the all modal dialogs.
   * - Updates the page key to trigger data re-fetching.
   */
  const reload = () => {
    sdsRegistryDialog.close();
    setProduct(null);
    setPageKey(prev => prev + 1);
  };

  /**
   * Opens the SDS Registry dialog and displays search results based on the provided row's chemical ID.
   *
   * @param {Object} row - The data object containing details for the specific row.
   * @param {string} row.chemical_id - The unique identifier for the chemical to be searched in the SDS Registry.
   */
  const showFoundSdsResults = (row) => {
    sdsRegistryDialog.open({
      searchParams: {
        id: row.chemical_id
      },
      data: [product, setProduct]
    });
  };

  const StatsProgress = () => {
    if (isLoadingStats) return ''
    return (
      <>
        <ProgressBarsWrapper style={{ marginBottom: '1rem' }}>
          {stats.progressbars.crawled.text}
          <ProgressBar value={stats.progressbars.crawled.percentage} displayValueTemplate={() => ''} />
        </ProgressBarsWrapper>
        <ProgressBarsWrapper style={{ marginBottom: '1rem' }}>
          {stats.progressbars.hits.text}
          <ProgressBar value={stats.progressbars.hits.percentage} displayValueTemplate={() => ''} />
        </ProgressBarsWrapper>
      </>
    );
  }

  /**
   * Renders the status body for SDS results based on the provided data row.
   *
   * @param {Object} row - The data object representing a single row.
   * @param {string} row.status - The status value used to determine styling and title.
   * @returns {JSX.Element} A JSX element representing the status body for the row.
   */
  const statusSdsResultsBody = (row) => {

    return <>
      <Status className={"status-" + row.status_flag}>
        <span className={"circle"}></span>
        <span className={"title"}>{row.status_text}</span>
      </Status>
    </>

  }

  /**
   * Renders a button to display SDS results with a formatted label and an icon.
   * The button triggers the `showFoundSdsResults` function, passing the provided row as an argument.
   *
   * @param {Object} row - The data object representing the row for which the SDS results are to be displayed.
   * @returns {JSX.Element} A styled button component with an icon and label.
   */
  const showSdsResultsBody = (row) => (
    <Button
      primary
      disabled={!!row?.has_registry}
      style={{ margin: 0 }}
      icon="pi pi-arrow-circle-right p-c p-button-icon-left"
      label={intl.formatMessage({ id: 'chemicalsds.registry.show_info_btn' })}
      onClick={(event) => {
        showFoundSdsResults(row);
      }}
    />
  );

  const CrawlerBody = (row) => (
    (row.crawler === 'google' && row.query.length > 0) ?
      <a
        href={`https://www.google.com/search?q=${encodeURIComponent(row.query)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {row.crawler}
      </a>
      :
      <>
        {row.crawler}
      </>
  );

  return (
    <PageConstraint>
      <ResponsiveSidebar
        blockScroll={true}
        position="right"
        visible={product}
        onHide={() => {
          setProduct(null)
        }}
        showCloseIcon={false}
      >
        <PdfRevision data={product} setData={setProduct} productId={sdsId} reload={reload} />
      </ResponsiveSidebar>
      <Modal
        modalController={sdsRegistryDialog}
        modalData={isLoading ? filteredSdsRegistries : []}
        ContentComponent={SdsRegisterDialog}
      />
      <PageHeader>

        <h1>{intl.formatMessage({ id: 'chemicalsds.registry.header' })}</h1>

        <ToolPanel>
        </ToolPanel>

        <TabView>
          <TabPanel
            header={intl.formatMessage({ id: 'chemicalsds.registry.tab_stats' })}
          >
            <Section>
              <StatsProgress />
            </Section>

            <DataTable
              responsive={true}
              className="clickable-rows"
              value={stats.dates}
              loading={isLoadingStats}
              paginator={true}
              rows="20"
            >
              <Column
                key="crawl_date"
                field="crawl_date"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.date' })}
                sortable={true}
              />
              <Column
                key="crawl_count"
                field="crawl_count"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.crawl_count' })}
                sortable={true}
              />
              <Column
                key="match_count"
                field="match_count"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.match_count' })}
                sortable={true}
              />
              <Column
                key="hit_percentage"
                field="hit_percentage"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.hit_percentage' })}
                sortable={true}
              />

            </DataTable>

          </TabPanel>

          <TabPanel
            header={intl.formatMessage({ id: 'chemicalsds.registry.tab_all' })}
          >
            <Section>
              <TableControls>
                <SearchInput value={inputFilter} onChange={handleInputFilter} />
                <Spacer />
                <span style={{ paddingRight: '10px' }} className="lg-show">
                  {intl.formatMessage({ id: 'common.show' })}
                </span>
                <SelectButton options={statusFilterOptions} value={statusFilter} onChange={updateStatusFilter} />
              </TableControls>
            </Section>

            <DataTable
              responsive={true}
              className="clickable-rows"
              value={filteredSdsRegistries}
              loading={isLoading}
              paginator={true}
              rows="20"
            >
              <Column
                key="chemical_id"
                field="chemical_id"
                header="ID"
                sortable={true}
              />
              <Column
                key="chemical_name"
                field="chemical_name"
                header={intl.formatMessage({ id: 'common.name' })}
                sortable={true}
              />
              <Column
                key="supplier"
                field="supplier"
                header={intl.formatMessage({ id: 'common.supplier' })}
                sortable={true}
              />
              <Column
                key="sds_date"
                field="sds_date"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.sds_date' })}
                sortable={true}
              />
              <Column
                key="status_text"
                field="status_text"
                header={intl.formatMessage({ id: 'common.status' })}
                sortable={false}
                body={statusSdsResultsBody}
              />
              <Column
                key="crawl_date"
                field="crawl_date"
                header={intl.formatMessage({ id: 'common.searched' })}
                sortable={true}
              />
              <Column
                key="file_date"
                field="file_date"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.found_date' })}
                sortable={true}
              />
              <Column
                key="show"
                header={intl.formatMessage({ id: 'chemicalsds.registry.show_info' })}
                sortable={false}
                body={showSdsResultsBody}
              />

            </DataTable>

          </TabPanel>

          <TabPanel
            header={intl.formatMessage({ id: 'chemicalsds.registry.tab_crawls' })}
          >
            <Section>
              <TableControls>
                <SearchInput value={inputFilter} onChange={handleInputFilter} />
              </TableControls>
            </Section>

            <DataTable
              responsive={true}
              className="clickable-rows"
              value={filteredCrawls}
              loading={isLoadingCrawls}
              paginator={true}
              rows="20"
            >
              <Column
                key="chemical_id"
                field="chemical_id"
                header="ID"
                sortable={true}
              />
              <Column
                key="chemical_name"
                field="chemical_name"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.chemical_name' })}
                sortable={true}
              />
              <Column
                key="chemical_supplier"
                field="chemical_supplier"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.chemical_supplier' })}
                sortable={true}
              />
              <Column
                key="language"
                field="language"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.language' })}
                sortable={true}
              />
              <Column
                key="crawler"
                field="crawler"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.crawler' })}
                sortable={false}
                body={CrawlerBody}
              />
              <Column
                key="date"
                field="date"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.date' })}
                sortable={true}
              />
              <Column
                key="matches"
                field="matches"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.matches' })}
                sortable={true}
              />

            </DataTable>

          </TabPanel>

          <TabPanel
            header={intl.formatMessage({ id: 'chemicalsds.registry.tab_orgs' })}
          >
            <Section>
              <TableControls>
                <div style={{ position: 'relative', width: '100%' }}>
                  <Dropdown
                    value={organisationId}
                    options={organisations}
                    optionLabel="label"
                    onChange={(e) => {
                      setOrganisationId(e.value);
                    }}
                    placeholder="-"
                    style={{ width: '100%' }}
                  />
                </div>
              </TableControls>
            </Section>
            {organisationId && !isLoadingOrgStats && (
              <>
                <Section>
                  <ProgressBarsWrapper style={{ marginBottom: '1rem' }}>
                    {orgStats.progressbars.crawled.text}
                    <ProgressBar value={orgStats.progressbars.crawled.percentage} displayValueTemplate={() => ''} />
                  </ProgressBarsWrapper>
                  <ProgressBarsWrapper style={{ marginBottom: '1rem' }}>
                    {orgStats.progressbars.hits.text}
                    <ProgressBar value={orgStats.progressbars.hits.percentage} displayValueTemplate={() => ''} />
                  </ProgressBarsWrapper>
                </Section>
                <Section>
                  <TableControls>
                    <SearchInput value={orgInputFilter} onChange={handleOrgInputFilter} />
                    <Spacer />
                    <span style={{ paddingRight: '10px' }} className="lg-show">
                      {intl.formatMessage({ id: 'common.show' })}
                    </span>
                    <SelectButton options={statusFilterOptions} value={orgStatusFilter} onChange={updateOrgStatusFilter} />
                  </TableControls>
                </Section>
              </>
            )}
            <DataTable
              responsive={true}
              className="clickable-rows"
              value={isOrgSelected ? filteredOrgChemicals : []}
              loading={isOrgSelected && isLoadingOrgChemicals}
              paginator={true}
              rows="20"
            >
              <Column
                key="chemical_id"
                field="chemical_id"
                header="ID"
                sortable={true}
              />
              <Column
                key="chemical_name"
                field="chemical_name"
                header={intl.formatMessage({ id: 'common.name' })}
                sortable={true}
              />
              <Column
                key="supplier"
                field="supplier"
                header={intl.formatMessage({ id: 'common.supplier' })}
                sortable={true}
              />
              <Column
                key="sds_date"
                field="sds_date"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.sds_date' })}
                sortable={true}
              />
              <Column
                key="status_text"
                field="status_text"
                header={intl.formatMessage({ id: 'common.status' })}
                sortable={false}
                body={statusSdsResultsBody}
              />
              <Column
                key="crawl_date"
                field="crawl_date"
                header={intl.formatMessage({ id: 'common.searched' })}
                sortable={true}
              />
              <Column
                key="file_date"
                field="file_date"
                header={intl.formatMessage({ id: 'chemicalsds.registry.table_headers.found_date' })}
                sortable={true}
              />
              <Column
                key="show"
                header={intl.formatMessage({ id: 'chemicalsds.registry.show_info' })}
                sortable={false}
                body={showSdsResultsBody}
              />

            </DataTable>

          </TabPanel>

        </TabView>

      </PageHeader>

    </PageConstraint>
  )
}
